
import { BaseModel } from './base.model';
import { AppDate } from './date.model';
import { Attribute } from './attribute.model';
import { Product } from './product.model';
import { Address } from './address.model';
import { OrderItemSku } from './order-item-sku.model';
import { OrderItemDelivery } from './order-item-delivery.model';
import {generateUUID, isEmptyArray, LooseInput} from '../shared/utils';
import { SKU } from './sku.model';
import {ECOMMERCE_TYPE, PREORDER_TYPE} from '../constants/order-types';
import {COMPLETE_STATE, VOID_STATE} from '../constants/order-states';
import {ORDER_ITEM_IN_PROGRESS} from '../constants/order-item-states';
import {Budget} from './budget.model';
import {FilterItem} from './filter-item.model';
import { PnpBudget } from './pnp-budget.model';

export const MAX_DELIVERY_QUANTITY = 999999;

export interface FinancialTotals {
    budget_id?: number | null;
    budget_total?: number;
    coupon_total?: number;
    delivery_total?: number;
    discount_total?: number;
    exempt_grand_total?: number;
    exempt_tax_total?: number;
    grand_total?: number;
    product_total?: number;
    subtotal?: number;
    tariff_total?: number;
    tax_total?: number;
}

type OrderItemInput = LooseInput<OrderItem> & {
    skus?: object[];
    deliveries?: object[];
    product?: object;
    chili_data?: unknown;
    customization_approved?: unknown;
    removedPresetAddressIds?: string[];
    uuid?: string;
    init_quantity?: number;
};

export class OrderItem extends BaseModel {
    public id: number;
    public order_id: number;
    public order_type: string;
    public entity_id: number;
    public needed_approval: boolean;
    public product_id: number;
    public total_weight: number;
    public custom: boolean;
    public state: string;
    public created_at: AppDate;
    public updated_at: AppDate;
    public completed_at: AppDate;

    public attrs: Array<Attribute>;

    // derived properties
    public order_key: string;
    public sku_id: number;
    public skus: OrderItemSku[];
    public quantity: number;
    public init_quantity: number;
    public product: Product;
    public financials_total: FinancialTotals;
    public budgets: PnpBudget[];
    public deliveries: OrderItemDelivery[];
    private originalDeliveries: OrderItemDelivery[];
    public notes: Array<object>;
    public originating_user: object;
    public vendor: string;
    public tariff_total: number;

    // public allAddresses: Address[];
    public hasSearchAddresses = false;
    public expanded = false;

    public uuid: string;
    public item_cut: boolean;

    public in_stock_dates:  {qty: number, date: AppDate | string}[];
    public backorderedMessage: string;
    public estimatedDates: string;

    public attr_attention: string;

    public changed = false;
    public hidden = false;  // show or hide on order confirmation or order history pages

    public addr_upload_file: string;
    public wslr_upload_file: string;
    public budgetItem: Budget;
    public shipment_timing: string;
    public expected_ship_at: string;

    is_tax_exempt: boolean;
    tax_exempt_reason: number;

    // Client-side only: preset/DL addresses whose row the user explicitly removed, so the
    // rebuild does not recreate them. Not part of the save payload (see prepItemToSave).
    public removedPresetAddressIds: string[] = [];


    constructor(data: OrderItemInput = {}) {
        super(data, {
            num: ['id', 'order_id', 'entity_id', 'product_id', 'total_weight', 'sku_id', 'quantity', 'tax_exempt_reason'],
            bool: ['needed_approval', 'custom', 'is_tax_exempt'],
            date: ['created_at', 'updated_at', 'completed_at']
        });

        if (data.product) {
            this.product = new Product(data.product);
        }


        if (!this.financials_total) {
           this.financials_total = {
               budget_id: null,
           };
        }
        if ( data.skus) {
            this.skus = data.skus.map( sku => new OrderItemSku(sku));
        } else {
            this.skus = [];
        }

        // _.each(this.skus, (sku, i) => {
        //     this.skus[i] = new OrderItemSku(sku);
        // });
        if (data.deliveries) {
            this.deliveries = data.deliveries.map( delivery => new OrderItemDelivery(delivery));
        } else  {
            this.deliveries = [];
        }

        // keep copy of original deliveries to find  deleted deliveries
        if ((data as OrderItem).originalDeliveries) {
            this.originalDeliveries = (data as OrderItem).originalDeliveries
        } else {
            this.originalDeliveries = [...this.deliveries.filter( d => !!d.method)];

        }

        this.removedPresetAddressIds = [...(data.removedPresetAddressIds || [])];

        if (this.state === 'COMPLETE_STATE') {
            this.state = 'ITEM_COMPLETE_STATE';
        }

        // this.allAddresses = data?.allAddresses || [];
        if (data.uuid) {
            // clone of order item
            this.uuid = data.uuid;
            this.init_quantity = data.init_quantity;
        } else {
            // data received from server
            this.uuid = generateUUID();
            this.init_quantity = this.quantity;
        }

        this.convertInStockDates();
        this.setBackorderMessage();
        this.calculateQuantity();

        if (this.hasTrackingData) {
            // reset shipping timing  and expected_ship_at if tracking data exists
            // this.expected_ship_at = null;
            this.shipment_timing =  null;
            this.backorderedMessage = null;
            this.estimatedDates = null;
        }

        if (this.expected_ship_at) {
            // reset shipping timing if expected_ship_at is defined
            this.shipment_timing = null;

            this.backorderedMessage = '';
            this.estimatedDates = ''
        }

        if (this.backorderedMessage && this.estimatedDates) {
            this.shipment_timing = null;
        }


        // workaround solution
        this.setProductAggregatedQuantity();

    }

    public getVariationSKU(skuID: number) {
        let foundSku: OrderItemSku = this.skus.find((sku: OrderItemSku) => sku.sku_id === skuID);

        if (!foundSku) {
            foundSku = new OrderItemSku({id: 0, sku_id: skuID, quantity: 0, price: 0, weight: 0, cartons: 0});
            this.skus.push(foundSku);
        }

        return foundSku;
    }

    public setVariationSKU(sku: OrderItemSku) {
        this.skus = this.skus.map(aSKU => (aSKU.sku_id === sku.sku_id) ? sku : aSKU);
    }

    // The items endpoint no longer embeds the product (see attachProduct): it is attached
    // separately from ElasticSearch, which can legitimately miss a product (not indexed yet,
    // or the /es/products/for-ids call failed and returned an empty list). A filter-less item
    // is preferable to throwing and taking the whole cart page down with it.
    public getFilters(): FilterItem[] {
        return this.product?.getFilters() || [];
    }

    public containsSelectedFilters(selectedFilters: FilterItem[]): boolean {
        if (isEmptyArray(selectedFilters)) {
            return true;
        }

        const productFilters = this.getFilters();
        return selectedFilters.some( filter => {

            if (!filter.selected) {
                return false;
            }

            return productFilters.some( pFilter => {
                return pFilter.id === filter.id;
            });
        });
    }

    public get quantityDelta() {
        return Math.max(this.quantity - this.init_quantity, 0);
    }

    public get aggregatedQuantity(): number {
        if (!this.product) {
            return 0;
        }
        return this.product.getAggregatedQuantity();
    }

    // total quantity is a combination of  product aggregated quantity and delta quantity
    public get totalQuantity() {
        return this.aggregatedQuantity + this.quantityDelta;
    }

    public prepItemToSave(): object {
        const deliveriesUUID = this.deliveries.map(d => d.uuid);

        const toBeDeleted = this.originalDeliveries.filter(
            (od) => !!od.id && !deliveriesUUID.includes(od.uuid)
        );

        // sent qty -1 for all deleted shipments
        toBeDeleted.forEach(d => d.quantity = -1);

        const deliveries = [...this.deliveries, ...toBeDeleted].map(delivery => delivery.prepareToSave() );

        // update budget value
        const financials: Omit<FinancialTotals, 'budget_id'> & { budget_id?: number | number[] } = {};

        if (this.product.budget_ids && this.product.budget_ids.length) {
            financials.budget_id = this.product.budget_ids[0];
        } else {
            financials.budget_id = [];
        }

        const obj =  {
            product_id: this.product_id,
            skus: this.skus,
            quantity: this.quantity,
            deliveries,
            financials_total: financials,
        };

        return obj;
    }

    public computeDeliveryTotalAmount() {

        this.calculateQuantity();

        if (!this.product.hasVariations) {
            if (this.skus && this.skus.length > 0) {
                this.skus[0].quantity = this.quantity;
            }
        }

    }

    /**
     * get actual deliveries that not removed
     */
    public getActualDeliveries() {
        return this.deliveries;
        // return this.deliveries ?
        //     this.deliveries.filter((delivery: OrderItemDelivery) => delivery.pendingDelete !== true) :
        //     [];
    }


    // attach a product fetched separately (the items endpoint no longer embeds it)
    // and re-run the product-dependent initialization the constructor would have done
    public attachProduct(product: Product) {
        this.product = product;
        this.calculateQuantity();
        this.setProductAggregatedQuantity();
    }

    public calculateQuantity() {
        this.quantity = this.getActualDeliveries().reduce( (total, i) => total += i.quantity, 0);

        if (!this.hasVariations && this.product) {
            const sku = this.product.firstSku;
            if (sku) {
                const skuId = sku.id;
                const orderItemSku = this.skus.find( s => s.sku_id === skuId);
                if (orderItemSku) {
                    orderItemSku.quantity = this.quantity;
                }
            }
        }
    }

    // get available quantity from provided or first available sku
    public getAvailableQuantity(s?: SKU) {
        const sku = this.getSKU(s);
        return (sku) ? sku.availableQuantity : 0;
    }

    // get pending quantity from provided or first available sku
    public getPendingQuantity(s?: SKU) {
        const sku = this.getSKU(s);
        return (sku) ? sku.pendingQuantity : 0;
    }

    public getInStockQuantity(s?: SKU): number {
        const sku = this.getSKU(s);
        return (sku) ? sku.inStockQuantity : 0;
    }

    private getSKU(sku?: SKU): SKU {
        if (sku) {
            return sku;
        }

        return (this.product) ? this.product.firstSku : null;
    }


    public get maxQuantity(): number {
        return this.product.hasInventory ?
            this.product.getMaxQuantity() :
            Math.max(this.product.getMaxQuantity() - this.totalQuantity, 0);
    }


    public get subTotal(): number {
        return (this.financials_total.subtotal || 0) - (this.financials_total.delivery_total || 0);
    }

    // public get shipping(): number {
    //     return this.financials_total.delivery_total || 0;
    // }

    public get shippingTotal(): number {
        if (isEmptyArray(this.deliveries)) {
            return 0;
        }
        return this.getActualDeliveries().reduce( (total, delivery) => total + (delivery.price || 0), 0);
    }

    public get taxes(): number {
        if (this.financials_total) {

            return this.taxExemptIncluded ?
                this.financials_total.exempt_tax_total :
                (this.financials_total.tax_total || 0);
        }
        return  0;
    }

    public get taxExemptIncluded(): boolean {
        return this.taxExempt && this.financials_total.exempt_tax_total != null;
    }

    public get totalCost(): number {
        if (this.financials_total) {
            return ((this.taxExempt && this.financials_total.exempt_grand_total != null) ?
                this.financials_total.exempt_grand_total :
                (this.financials_total.grand_total || 0));
        }
        return 0;
    }


    public get discount(): number {
        return this.financials_total.discount_total || 0;
    }

    public get couponDiscount(): number {
        return this.financials_total.coupon_total || 0;
    }

    public get totalDiscount(): number {
        return this.discount + this.couponDiscount;
    }

    public get budget(): number {
        return 0;
        // should be  redone
        // return this.financials_total.budget_total || 0;
    }

    public get maxAvailableQuantity(): number {

        const product = this.product;

        if (product.inventoryType) { // product type INVENTORY_CHILDs
            return product.getAvailableQuantity() + this.init_quantity;
        }


        if (this.product.hasInventory) {
            const maxQuantity = this.maxQuantity + this.init_quantity;
            if (maxQuantity > 0 ) {
                return maxQuantity;
            }
        }
    }



    private setBackorderMessage(): string {
        this.backorderedMessage = '';
        this.estimatedDates = '';

        if (!Array.isArray(this.in_stock_dates) || this.in_stock_dates.length === 0) {
            return;
        }

        // set backorder message
        const arrivingNowCount = this.in_stock_dates.filter( i => i.date === 'now').length;
        if (arrivingNowCount === 0) {
            this.backorderedMessage = 'On Order:';
        } else if (arrivingNowCount < this.in_stock_dates.length) {
            this.backorderedMessage = 'Partially On Order:';
        }

        // set unique dates
        const dates = this.in_stock_dates.map( i => {
            if (typeof  i.date === 'string' ) {
                return 'now';
            }

            if (i.date.isInPast) {
                return 'now';
            }

            return i.date.longDateFormatted;
        }).filter((v, i, a) => a.indexOf(v) === i); // only unique data

        const formattedDates = dates.map( d =>  d === 'now' ? 'Soon' : d).join(' and ');
        const estimated = (dt: string[]) => dt.length > 1 ? 'Estimated Ship Dates' : 'Estimated Ship Date';
        this.estimatedDates = `${estimated(dates)}: ${formattedDates}`;
    }

    private convertInStockDates() {
        if (!this.in_stock_dates) {
            return [];
        }

        this.in_stock_dates = this.in_stock_dates.map( i => {
            return {
                qty: i.qty,
                date: typeof i.date === 'string' ? i.date : new AppDate(i.date)
            }
        });
    }
    public updateDelivery(delivery: OrderItemDelivery | number | string) {
        if (!delivery) {
            return;
        }

        if ( typeof  delivery === 'number') {
            // remove order from list
            this.deliveries =  this.deliveries.filter( i => i.id !== delivery);
        } else if (typeof  delivery === 'string') {
            this.deliveries =  this.deliveries.filter( i => i.uuid !== delivery);
        } else {

            let idx = -1;
            if (delivery.id) {
                idx = this.deliveries.findIndex( i => i.id === delivery.id);
            } else {
                idx = this.deliveries.findIndex( i => i.uuid === delivery.uuid);
            }

            // the address is in use again, so it must no longer count as explicitly removed
            if (delivery.uniqueAddressId) {
                this.removedPresetAddressIds = this.removedPresetAddressIds
                    .filter(id => id !== delivery.uniqueAddressId);
            }

            // search by id or addr_id in case id is not defined

            if (idx !== -1) {
                // update order in list
                this.deliveries = Object.assign([...this.deliveries], {[idx]: delivery});
            } else {
                // add order to list
                this.deliveries = [...this.deliveries, delivery];
            }
        }
        this.changed = true;
        this.calculateQuantity();
    }

    // check if the quantity available, otherwise reduce it
    public checkForAvailableItems() {
        if (this.product.type === 'SKU_PRODUCT') {
            return;
        }

        if (!this.product.hasVariations) {

            const availableQuantity = this.product.getAvailableQuantity();
            if (availableQuantity < this.quantity) {
                const ratio = availableQuantity / this.quantity;
                this.deliveries.forEach( d => d.quantity = Math.floor( d.quantity * ratio))
                const totalQty = this.deliveries.reduce( (total, i) => (total += i.quantity), 0);
                this.quantity = totalQty;
                if (this.skus && this.skus.length > 0) {
                    this.skus[0].quantity = totalQty;
                }
            }

        } else {
            this.product.skus.forEach( sku => {
                const orderSku = this.skus.find( s => s.sku_id === sku.id);
                if (orderSku && orderSku.quantity > sku.available_quantity) {
                    orderSku.quantity = sku.available_quantity;
                }
            });


            const totalQty =  this.skus.reduce( (total, i) => (total += i.quantity), 0);
            if (this.deliveries.length > 0) {
                this.deliveries[0].quantity = totalQty;
            }
            this.quantity =  totalQty;
        }
    }


    // public get price(): number {
    //
    //     if (!this.product) {
    //         return 0;
    //     }
    //
    //     if (this.product.hasVariations) {
    //         let itemPrice = 0;
    //         if (Array.isArray(this.skus)) {
    //             this.skus.forEach( sku => {
    //                 const quantity = sku.quantity || 0;
    //                 const productSKU = this.product.getSKUbyID(sku.sku_id);
    //                 itemPrice += this.product.getPrice(quantity, productSKU);
    //             })
    //         }
    //
    //         return itemPrice;
    //     }
    //
    //     if (Array.isArray(this.product.skus) && this.product.skus.length > 0) {
    //         const productSKU = this.product.skus[0];
    //         return this.product.getPrice(this.totalQuantity, productSKU);
    //     }
    //
    //     return 0;
    // }

    public get hasVariations(): boolean {
        if (!this.product) {
            return false;
        }

        return this.product.hasVariations;
    }

    // only for product without variations
    public get productSkuId(): string {
        if (!this.product) {
            return '';
        }
        return this.product.firstSkuId;
    }


    // public get productPrice(): number {
    //     const quantity = (this.order_type === PREORDER_TYPE) ?
    //         this.totalQuantity : this.quantity;
    //
    //     return this.product.getPrice(quantity);
    // }

    public orderedQuantity() {
        return (this.order_type === PREORDER_TYPE) ?
            this.totalQuantity : this.quantity;
    }

    // sku is used for variations
    public getProductPrice(sku?: SKU): number {
        if (!this.orderedQuantity || isEmptyArray(this.skus)) {
            // zero quantity - price should be taken from sku price tiers
            return this.product.getPrice(sku);
        }

        if (!sku) {
            // non variation product
                // no order item sku are defined
            const productPrice  = Number(this.skus[0].product_price);
            return  productPrice ? productPrice : this.product.getPrice(null);

        } else {
            // variations
            // find order item sku
            const orderItemSku = this.skus.find(s => s.sku_id === sku.id);
            if (!orderItemSku) {
                return 0;
            }

            const productPrice  = Number(orderItemSku.product_price);
            return  productPrice ? productPrice : this.product.getPrice(sku);
        }
    }


    // public get groupAggregatedQuantity(): number {
    //     return  (this.product.skus[0].groupMinQTY > 0 && this.product.skus[0].group_aggregated_quantity > -1)
    //         ? this.product.skus[0].group_aggregated_quantity : -1;
    //
    // }

    public get deliveriesTotalQuantity(): number {
        const qty = this.deliveries.reduce((total, delivery) => total + Number(delivery.quantity), 0);

        if (this.skus?.length === 1) {
            this.skus[0].quantity = this.quantity;
        }

        return qty;
    }


    public get canPurchase(): boolean {
        return this.isAvailable;
    }

    public get isAvailable(): boolean {
        return this.deliveries.length !== 0 &&
            (  this.order_type === ECOMMERCE_TYPE
                // On-Demand cart => only On-Demand products. A By-Window item (window_id > 0) must never be
                // orderable here (AB#84800): a clean OD search never returns it, so its presence is stale/invalid.
                ? (this.product.isOnDemand && this.isODAvailable)
                : this.product.ow_purchasable === true );
    }

    private get isODAvailable(): boolean {
        return  (this.quantityDelta !== 0 || this.product.inStock ) ||
            (!this.product.inStock && this.init_quantity > 0)
    }


    public get isCancelled(): boolean {
        return this.state === VOID_STATE;
    }

    public get isCompleted(): boolean {
        return this.state === COMPLETE_STATE;
    }

    public get isEditable(): boolean {
        return !this.state || this.state === ORDER_ITEM_IN_PROGRESS;
    }


    public computeAvailableAddresses() {
        // if (isWSLR) {
        //     this.availableAddresses = [];
        //     return this.availableAddresses;
        // }
        //
        // const selectedAddresses = this.deliveries
        //     .map((delivery) => {
        //         return delivery.getAddress().longAddress;
        //     });
        //
        // this.availableAddresses = this.allAddresses.filter((address) => {
        //     return (selectedAddresses.includes(address.longAddress)) ? null : address;
        // });
        //
        // return this.availableAddresses
    }

    // remove all deliveries that don't have id
    public cleanNewDeliveries() {
        this.deliveries =  this.deliveries.filter( d => d.id > 0);
    }

    public get isOnDemandOrder(): boolean {
        return this.order_type === ECOMMERCE_TYPE;
    }

    public get isBuyingWindowOrder(): boolean {
        return this.order_type === PREORDER_TYPE;
    }

    // find delivery by id or uuid
    public findDelivery( id: number | string | Address): OrderItemDelivery {
        if (!id) {
            return null;
        }

        if (id instanceof  Address) {
          return this.deliveries.find( d => d.getAddress().longAddress === id.longAddress);
        } else if (typeof id === 'number') {
            return this.deliveries.find( i => i.id === id);
        } else {
            return this.deliveries.find( i => i.uuid === id);
        }
    }

    public removeDelivery(delivery: number | string | OrderItemDelivery) {
        if (typeof  delivery === 'number' || typeof delivery === 'string') {
            this.updateDelivery(delivery);
        } else {
            // Remember the explicit removal so setupDistributionListDeliveries() does not
            // recreate the row from the order's preset/DL addresses (AB#86452).
            if (delivery.uniqueAddressId && !this.removedPresetAddressIds.includes(delivery.uniqueAddressId)) {
                this.removedPresetAddressIds.push(delivery.uniqueAddressId);
            }
            this.updateDelivery(delivery.uuid);
        }

    }

    public validateDeliveryQuantity(delivery: OrderItemDelivery, newQuantity: number): number {
        let result = newQuantity;

        if (result > MAX_DELIVERY_QUANTITY) {
            result = MAX_DELIVERY_QUANTITY
        }

        // if (this.product.inventoryType) { // product type INVENTORY_CHILDs
        //
        //     const availableQty = this.product.getAvailableQuantity() + this.init_quantity;
        //
        //     if (availableQty < delivery.quantity) {
        //         result = availableQty;
        //     }
        // }

        if (this.product.hasInventory) {
            const maxQuantity = this.maxQuantity + this.init_quantity;
            if (maxQuantity > 0 && maxQuantity < result) {
                result = maxQuantity;
            }
        }



        return result;

    }

    public validateVariationQuantity(sku: SKU, allocationGroupAvailableQty?: number) {
        const itemSKU = this.getVariationSKU(sku.id);

        if (itemSKU.quantity < 0) {
            itemSKU.quantity = 0;
        }

        if (itemSKU.quantity > MAX_DELIVERY_QUANTITY) {
            itemSKU.quantity = MAX_DELIVERY_QUANTITY;
        }

        if (this.product.inventoryType) {
            const selectedAvailableQty = Number(allocationGroupAvailableQty);
            const limitBaseQty = Number.isFinite(selectedAvailableQty) ? selectedAvailableQty : sku.available_quantity;
            const availableQty = Math.max((limitBaseQty + itemSKU.init_quantity) || 0, 0);

            if (availableQty < itemSKU.quantity) {
                itemSKU.quantity = availableQty;
                this.setVariationSKU(itemSKU);
            }
        }

        const quantity = this.skus.reduce( (total, skuItem) => total += skuItem.quantity, 0);

        this.deliveries.forEach( delivery => delivery.quantity = quantity);
    }


    public get hasTrimesterBudget(): boolean {
        return this.budgetItem && this.budgetItem.trimester === true;
    }

    public get productDiscount(): number {
        if (this.hasTrimesterBudget) {
            return 0;
        }
        return this.product.discount;
    }

    public getProductDiscount(sku?: SKU): number | undefined {

        if (isEmptyArray(this.skus)) {
            return;
        }

        if (this.hasDiscountErrors) {
            return;
        }

        let orderItemSku: OrderItemSku;
        if (!sku) {
            // take from first sku
            orderItemSku = this.skus[0];
        } else {
            orderItemSku = this.skus.find(s => s.sku_id === sku.id);
            if (!orderItemSku) {
               return;
            }
        }

        if (!orderItemSku.discount_price) {
            return;
        }

        // parse to number
        const discount = Number(orderItemSku.discount_price);
        if (isNaN(discount)) {
            return;
        }
        return discount;
    }



    public resetFinancialTotals() {
        this.financials_total = {
            budget_id: null,
            budget_total: 0,
            coupon_total: 0,
            delivery_total: 0,
            discount_total: 0,
            grand_total: 0,
            product_total: 0,
            subtotal: 0,
            tariff_total: 0,
            tax_total: 0,
        };

        // reset delivery price
        if (!isEmptyArray(this.deliveries)) {
            this.deliveries.forEach( d =>  d.price = 0);
        }
    }

    // Normalize a clone of a past order item into a fresh, purchasable line for reorder.
    // A cancelled/shipped source item carries state (e.g. VOID) and per-delivery shipment
    // residue; without clearing it the reorder modal renders the line as cancelled
    // ("Shipment has been cancelled for this item."), blocks normal editing and saves an
    // empty (qty 0) line. Drop the cancelled/complete state, shipment/tracking data, and
    // treat it as a brand-new line with no prior deliveries to diff against on save.
    public prepareForReorder() {
        this.state = null;
        this.init_quantity = 0;

        this.deliveries.forEach( d => {
            d.state = null;
            d.tracking_number = null;
            d.shipped_at = null;
        });

        this.originalDeliveries = [];
    }

    public get taxExempt(): boolean {
        return this.is_tax_exempt === true;
    }

    public get hasTrackingData(): boolean {
        return this.deliveries.some( d => !!d.tracking_number);
    }

    public get taxExemptReason(): string {
        if (this.taxExempt) {
            return '';
        }

        if (this.tax_exempt_reason === 0 || this.tax_exempt_reason === 1) {
            return 'No Tax Exemption exists on file with this Vendor';
        }

        if (this.tax_exempt_reason === 2) {
            return 'No Tax Exemption exists on file to this location';
        }

        return  '';
    }

    get restricted(): boolean {
        return this.product?.restricted || false;
    }

    // aggregated quantity for on-demand product is not defined yet
    // this is a temporary solution
    public setProductAggregatedQuantity() {
        if (!this.product) {
            return;
        }

        if (this.product.isBuyingWindow) {
            return;
        }

        if (this.product.hasVariations) {
            this.product.skus.forEach( productSKU => {
                const itemSKU = this.skus.find( orderSKU => orderSKU.sku_id === productSKU.id);
                if (!productSKU.aggregated_quantity) {
                    productSKU.aggregated_quantity = itemSKU?.quantity || 0;
                }
            });
        } else {
            // product may be a shared cached instance (see product.service.ts getProductsByIds),
            // so only set this once to avoid one item's init_quantity clobbering another's
            if (this.product.firstSku && !this.product.firstSku.aggregated_quantity) {
                this.product.firstSku.aggregated_quantity = this.init_quantity;
            }
        }
    }

    public validateAddressesAndShipmentMethodsError(): string {
        if (this.deliveries.some( delivery => !delivery.addr_id)) {
            return  'Shipment address should be selected';
        }

        if (this.deliveries.some( delivery => !delivery.method && delivery.quantity > 0)) {
            return ('Empty shipping method is not allowed');
        }

        return '';

    }

    public containsDeliveryByAddress(address: Address): boolean {
        if (isEmptyArray(this.deliveries)) {
            return false;
        }

        return this.deliveries.some( d =>  {
            return d.quantity > 0  && d.addr_id === address.id && d.address_type === address.address_type;
        });
    }

    public resetOriginalDeliveries() {
        this.originalDeliveries = [...this.deliveries.filter( d => !!d.method)];
    }

    // public get selectedAddresses(): string[] {
    //     return this.deliveries.map( d => d.getAddress().longAddress);
    // }
    //
    // public get availableAddresses(): Address[] {
    //     const selectedAddresses = this.selectedAddresses;
    //
    //     return this.allAddresses.filter( a => {
    //         return !selectedAddresses.includes(a.longAddress)
    //     })
    // }

    get hasDiscountErrors(): boolean {
        if (isEmptyArray(this.deliveries)) {
            return false;
        }


        return this.deliveries.some( d => {
            return d.discount_code?.hasError === true;
        })
    }

    // checks  if product has customizations
    get productHasCustomization(): boolean {
      return this.product?.isCustomizable === true;
    }

    // checks if order item has been customized
    get isCustomized(): boolean {
      return this.productHasCustomization && !isEmptyArray(this.deliveries) && this.deliveries.some( d => !!d.isCustomized);
    }

    // checks if all deliveries that are actually being ordered are customized
    // (useful for validation before saving).
    // Only deliveries with a quantity are considered: in List View every preset / distribution list
    // address keeps a delivery row even at quantity 0 so its column is not collapsed, and an address
    // the user is not ordering to must not require a customization.
    get areAllDeliveriesCustomized(): boolean {
      if (!this.productHasCustomization) {
        return true; // not a customizable product, so no customization required
      }
      if (isEmptyArray(this.deliveries)) {
        return false; // no deliveries to check
      }
      const orderedDeliveries = this.deliveries.filter( d => d.quantity > 0);
      if (isEmptyArray(orderedDeliveries)) {
        return true; // nothing is being ordered, so there is nothing left to customize
      }
      return orderedDeliveries.every( d => !!d.isCustomized);
    }

    get canCustomize(): boolean {
      return this.productHasCustomization && this.isEditable;
    }

    get isNew(): boolean {
      return !this.id;
    }

    get hasBudget(): boolean {
        // should be redone
        // return !isEmptyArray(this.budgets);
      return false;
    }

}
